import React, { useState, useMemo, useEffect } from 'react';
import { Card, Form, Input, Button, Alert, Typography, Checkbox } from 'antd';
import { UserOutlined, LockOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import AuthLayout from '../../layouts/AuthLayout';
import sthenusLogo from '../../assets/sthenus-logo.png';
import { useAuth } from '../../hooks/useAuth';

const { Text } = Typography;

export default function LoginPage() {
    const [loading, setLoading] = useState(false);
    const [globalError, setGlobalError] = useState(null);
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const { login, isAuthenticated, loading: authLoading } = useAuth();

    const mockEnabled = useMemo(
        () => (process.env.REACT_APP_ENABLE_MOCK_AUTH ?? 'true') !== 'false',
        []
    );

    const handleInputChange = () => {
        if (globalError) {
            setGlobalError(null);
        }
    };

    useEffect(() => {
        if (!authLoading && isAuthenticated) {
            navigate('/', { replace: true });
        }
    }, [authLoading, isAuthenticated, navigate]);

    const onFinish = async (values) => {
        setLoading(true);
        setGlobalError(null);
        
        try {
            await login({
                email: values.email,
                password: values.password,
                remember: values.remember,
            });
            
            navigate('/');
        } catch (err) {
            console.error('Login error:', err); 
            console.log('Error details:', {
                status: err?.response?.status,
                data: err?.response?.data,
                code: err?.response?.data?.code,
                message: err?.response?.data?.message
            });
            
            const status = err?.response?.status;
            const data = err?.response?.data;
            const errorCode = data?.code || err?.code;
            const errorMessage = data?.message || err?.message || 'Error desconocido';

            form.setFields([]);

            // Manejar errores específicos por código
            if (errorCode === 'INVALID_CREDENTIALS') {
                setGlobalError('Email o contraseña incorrectos. Verifica tus credenciales e inténtalo de nuevo.');
            } else if (status === 400 || status === 401 || status === 422) {
                // Errores de validación del servidor
                const fieldErrors = data?.fieldErrors || {};
                const fields = Object.entries(fieldErrors).map(([name, errors]) => ({
                    name,
                    errors: Array.isArray(errors) ? errors : [String(errors || 'Error')],
                }));

                if (fields.length) {
                    form.setFields(fields);
                } else {
                    setGlobalError(errorMessage === 'Unknown error' ? 'Credenciales incorrectas. Verifica tu email y contraseña.' : errorMessage);
                }
            } else if (status === 403) {
                setGlobalError('Tu cuenta no tiene permisos para acceder. Contacta al administrador.');
            } else if (status === 429) {
                setGlobalError('Demasiados intentos de inicio de sesión. Espera unos minutos antes de intentar de nuevo.');
            } else if (status >= 500) {
                setGlobalError('Error del servidor. Por favor, inténtalo más tarde.');
            } else if (err.code === 'NETWORK_ERROR' || err.code === 'VALIDATION_ERROR' || !navigator.onLine) {
                setGlobalError('Sin conexión a internet. Verifica tu conexión e inténtalo de nuevo.');
            } else {
                // Si el mensaje es "Unknown error", mostrar un mensaje más amigable
                const friendlyMessage = errorMessage === 'Unknown error' 
                    ? 'Ha ocurrido un error inesperado. Por favor, inténtalo de nuevo.' 
                    : errorMessage;
                setGlobalError(friendlyMessage);
            }
        } finally {
            setLoading(false);
        }
    };

    const cardStyle = {
        width: '100%',
        maxWidth: 480,
        borderRadius: 16,
        boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
    };

    const logoStyle = {
        display: 'block',
        margin: '0 auto 8px',
        width: 160,
        height: 'auto',
        objectFit: 'contain',
    };

    return (
        <AuthLayout>
            <Card style={cardStyle} bodyStyle={{ padding: 24 }}>
                <img src={sthenusLogo} alt="Sthenus" style={logoStyle} />

                <div style={{ textAlign: 'center', marginBottom: 16 }}>
                    <Text type="secondary">Bienvenido de vuelta</Text>
                </div>

                {mockEnabled && (
                    <Alert
                        type="warning"
                        showIcon
                        style={{ marginBottom: 16 }}
                        message="Modo MOCK activado"
                        description="Usa cualquier email y contraseña."
                    />
                )}

                {globalError && (
                    <Alert
                        type="error"
                        showIcon
                        icon={<ExclamationCircleOutlined />}
                        style={{ marginBottom: 16 }}
                        message="Error de inicio de sesión"
                        description={globalError}
                        closable
                        onClose={() => setGlobalError(null)}
                    />
                )}

                <Form
                    form={form}
                    name="login"
                    layout="vertical"
                    requiredMark={false}
                    onFinish={onFinish}
                    validateTrigger={['onBlur', 'onSubmit']}
                    initialValues={{ remember: true }}
                >
                    <Form.Item
                        label="Email"
                        name="email"
                        rules={[
                            { required: true, message: 'El email es obligatorio.' },
                            { type: 'email', message: 'Formato de email inválido.' },
                            { min: 5, message: 'El email debe tener al menos 5 caracteres.' },
                            { max: 100, message: 'El email no puede exceder 100 caracteres.' },
                        ]}
                    >
                        <Input
                            size="large"
                            prefix={<UserOutlined aria-hidden="true" />}
                            placeholder="tu@email.com"
                            aria-label="Correo electrónico"
                            autoComplete="email"
                            onChange={handleInputChange}
                        />
                    </Form.Item>

                    <Form.Item
                        label="Contraseña"
                        name="password"
                        rules={[
                            { required: true, message: 'La contraseña es obligatoria.' },
                            { min: 6, message: 'La contraseña debe tener al menos 6 caracteres.' },
                            { max: 50, message: 'La contraseña no puede exceder 50 caracteres.' },
                        ]}
                    >
                        <Input.Password
                            size="large"
                            prefix={<LockOutlined aria-hidden="true" />}
                            placeholder="Tu contraseña"
                            aria-label="Contraseña"
                            autoComplete="current-password"
                            onChange={handleInputChange}
                        />
                    </Form.Item>

                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            marginBottom: 8,
                        }}
                    >
                        <Form.Item name="remember" valuePropName="checked" noStyle>
                            <Checkbox>Recordarme</Checkbox>
                        </Form.Item>
                        <a href="#/forgot" aria-label="¿Olvidaste tu contraseña? (no implementado)">
                            ¿Olvidaste tu contraseña?
                        </a>
                    </div>

                    <Form.Item style={{ marginTop: 8 }}>
                        <Button
                            type="primary"
                            htmlType="submit"
                            size="large"
                            block
                            loading={loading}
                            aria-label="Iniciar sesión"
                        >
                            Iniciar Sesión
                        </Button>
                    </Form.Item>

                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            gap: 16,
                            marginBottom: 16,
                            fontSize: 12,
                        }}
                    >
                        <a href="/privacy-policy" onClick={(e) => { e.preventDefault(); navigate('/privacy-policy'); }}>
                            Política de Privacidad
                        </a>
                        <span style={{ color: '#d9d9d9' }}>|</span>
                        <a href="/terms-and-conditions" onClick={(e) => { e.preventDefault(); navigate('/terms-and-conditions'); }}>
                            Términos y Condiciones
                        </a>
                        <span style={{ color: '#d9d9d9' }}>|</span>
                        <a href="/support" onClick={(e) => { e.preventDefault(); navigate('/support'); }}>
                            Centro de ayuda
                        </a>
                        <span style={{ color: '#d9d9d9' }}>|</span>
                        <a href="/eliminar-datos" onClick={(e) => { e.preventDefault(); navigate('/eliminar-datos'); }}>
                            Eliminar cuenta
                        </a>
                    </div>
                </Form>                
            </Card>
        </AuthLayout>
    );
}
